// Чиста логіка (без JSX) для сторінки /allocation/import: розпізнавання
// колонок файлу рейтингу за заголовком, перевірка балів, зведення звіту
// (сума проти показника моделі) та побудова payload для POST /api/dss/analyze.
// Винесено в окремий .mjs-модуль, щоб цю логіку можна було виконати напряму
// через `node`/`import()` у тестах без збірки Babel/JSX.

export const SCALE_MAX = { ses: 50, dorm: 25, act: 25 };
// Нижні межі шкал. Сесія -- 30..50: порожньо, 0 або менше 30 означає «без
// сесії» (попередження, а не помилку): значення йде в DSS як є, і DSS ставить
// такого здобувача в окрему групу no_session після загального конкурсу.
export const SCALE_MIN = { ses: 30, dorm: 0, act: 0 };
// Віддаленість q і пільга p -- рівні 1..3 (стаття, табл. 1).
export const ZONE_RANGE = { min: 1, max: 3 };
export const PRIVILEGE_RANGE = { min: 1, max: 3 };

// Порядок важливий лише для читабельності -- detectColumns перевіряє кожен
// заголовок проти усіх патернів і зупиняється на першому збігу, тому патерн
// "діяльність" (негативний lookahead на "гурт") і "гуртожиток" між собою
// не конфліктують незалежно від порядку перебору.
export const COLUMN_PATTERNS = {
  pib: /П\.?І\.?Б|прізвищ/i,
  room: /кімнат/i,
  ses: /МАХ\s*50|сес/i,
  dorm: /МАХ\s*25.*гурт/i,
  act: /МАХ\s*25(?!.*гурт)/i,
  // Необов'язкові колонки (дзеркально до tools/ranking_file.py у Gurt_Systemts_dss).
  zone: /віддален|зона/i,
  privilege: /пільг/i,
  // "Акти"/"актів", але не "Активність" (JS \b не працює з кирилицею).
  acts: /(^|[^а-яіїєґ])акт(и|ів|ам)?($|[^а-яіїєґ])/i,
};

export const COLUMN_LABELS = {
  pib: "ПІБ",
  room: "Кімната",
  ses: "Сесія (30–50)",
  dorm: "Гуртожиток (MAX 25)",
  act: "Діяльність (MAX 25)",
  zone: "Віддаленість (1–3)",
  privilege: "Пільга (1–3)",
  acts: "Акти",
};

export const REQUIRED_COLUMNS = ["pib", "room", "ses", "dorm", "act"];
export const OPTIONAL_COLUMNS = ["zone", "privilege", "acts"];
export const SCORE_COLUMNS = ["ses", "dorm", "act"];

export const DEFAULT_PRIVILEGE = 1;

function cellText(cell) {
  return cell === null || cell === undefined ? "" : String(cell).trim();
}

// Визначає, яка колонка (за індексом) відповідає кожному з REQUIRED_COLUMNS
// та необов'язкових OPTIONAL_COLUMNS (zone, privilege; не входять у missing),
// за текстом заголовка (регулярні вирази), а не за номером колонки.
export function detectColumns(headerRow) {
  const indices = { pib: null, room: null, ses: null, dorm: null, act: null, zone: null, privilege: null, acts: null };
  const foundHeaders = [];
  // Паралельний до foundHeaders масив реальних індексів колонок -- потрібен
  // для ручного зіставлення, бо порожні клітинки заголовка пропускаються і
  // позиція в foundHeaders може не збігатися з номером колонки.
  const headerEntries = [];

  (headerRow || []).forEach((cell, colIndex) => {
    const text = cellText(cell);
    if (!text) return;
    foundHeaders.push(text);
    headerEntries.push({ index: colIndex, text });
    for (const key of [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS]) {
      if (indices[key] === null && COLUMN_PATTERNS[key].test(text)) {
        indices[key] = colIndex;
        break;
      }
    }
  });

  const missing = REQUIRED_COLUMNS.filter((key) => indices[key] === null);
  return { indices, foundHeaders, headerEntries, missing };
}

// Сканує перші maxScan рядків аркуша і повертає той, що найкраще підходить
// на роль рядка заголовків (найбільше розпізнаних колонок), вимагаючи
// щонайменше 3 розпізнані колонки (три бали + хоча б одна з ПІБ/кімната).
export function findHeaderRow(rows, maxScan = 20) {
  let best = null;
  const limit = Math.min((rows || []).length, maxScan);

  for (let r = 0; r < limit; r += 1) {
    const row = rows[r] || [];
    const { indices, foundHeaders, headerEntries, missing } = detectColumns(row);
    const scoreCount = REQUIRED_COLUMNS.length - missing.length;
    if (scoreCount >= 3 && (!best || scoreCount > best.scoreCount)) {
      best = {
        rowIndex: r,
        headerRow: row,
        indices,
        foundHeaders,
        headerEntries,
        missing,
        scoreCount,
      };
      if (scoreCount === REQUIRED_COLUMNS.length) break;
    }
  }

  return best;
}

function parseNumericCell(raw) {
  if (raw === null || raw === undefined) return { isEmpty: true, value: 0 };
  const text = typeof raw === "string" ? raw.trim() : raw;
  if (text === "") return { isEmpty: true, value: 0 };
  const num = Number(text);
  if (Number.isNaN(num)) return { isEmpty: true, value: 0 };
  return { isEmpty: false, value: num };
}

// Рівень 1..3 з клітинки: { level, original, clamped } або null, якщо значення
// порожнє, нечислове, не ціле або менше 1. Значення понад 3 -> 3 (clamped).
function parseLevel(raw, range) {
  const { isEmpty, value } = parseNumericCell(raw);
  if (isEmpty || !Number.isInteger(value) || value < range.min) return null;
  if (value > range.max) return { level: range.max, original: value, clamped: true };
  return { level: value, original: value, clamped: false };
}

// Класифікує один рядок:
//  - dorm/act: порожнє -> 0 (issue "empty"); понад SCALE_MAX -> максимум
//    (issue "clamped" з original, рішення Р2); від'ємне -> рядок виключається
//    ("out_of_range");
//  - ses (шкала 30..50): порожнє -> 0, а 0..<30 -- як є, обидва з issue
//    "no_session" (попередження, рядок лишається; DSS рахує такого здобувача
//    в окремій групі «без сесії»); понад 50 -> 50 ("clamped"); від'ємне ->
//    "out_of_range";
//  - усі три бали порожні -> "no_scores" (відрізняється від одного порожнього);
//  - zone: ціле 1..3; понад 3 -> 3 ("clamped"); порожнє, 0, дріб, текст ->
//    values.zone = null з issue "zone_missing" (рядок лишається, доки не
//    задано віддаленість -- див. applyDefaultZone);
//  - privilege: ціле 1..3; понад 3 -> 3 ("clamped"); порожнє або нерозпізнане
//    -> 1 з issue "privilege_default".
export function classifyRow(rawValues) {
  const values = {};
  const issues = [];
  let emptyCount = 0;

  SCORE_COLUMNS.forEach((field) => {
    const { isEmpty, value } = parseNumericCell(rawValues?.[field]);
    if (isEmpty) {
      emptyCount += 1;
      issues.push({ field, type: field === "ses" ? "no_session" : "empty" });
      values[field] = 0;
    } else if (value < 0) {
      issues.push({ field, type: "out_of_range" });
      values[field] = value;
    } else if (value > SCALE_MAX[field]) {
      issues.push({ field, type: "clamped", original: value });
      values[field] = SCALE_MAX[field];
    } else if (value < SCALE_MIN[field]) {
      // Лише для ses (для dorm/act SCALE_MIN = 0, а від'ємні відсіяно вище).
      issues.push({ field, type: "no_session", original: value });
      values[field] = value;
    } else {
      values[field] = value;
    }
  });

  const zone = parseLevel(rawValues?.zone, ZONE_RANGE);
  if (zone === null) {
    issues.push({ field: "zone", type: "zone_missing" });
    values.zone = null;
  } else {
    values.zone = zone.level;
    if (zone.clamped) issues.push({ field: "zone", type: "clamped", original: zone.original });
  }

  const privilege = parseLevel(rawValues?.privilege, PRIVILEGE_RANGE);
  if (privilege === null) {
    issues.push({ field: "privilege", type: "privilege_default" });
    values.privilege = DEFAULT_PRIVILEGE;
  } else {
    values.privilege = privilege.level;
    if (privilege.clamped) issues.push({ field: "privilege", type: "clamped", original: privilege.original });
  }

  const hasOutOfRange = issues.some((i) => i.type === "out_of_range");
  const noScoresAtAll = emptyCount === SCORE_COLUMNS.length;

  let excludeReason = null;
  if (noScoresAtAll) excludeReason = "no_scores";
  else if (hasOutOfRange) excludeReason = "out_of_range";

  return { values, issues, excluded: excludeReason !== null, excludeReason };
}

// Задає віддаленість за замовчуванням ЛИШЕ рядкам з issue "zone_missing":
// values.zone = zone, "zone_missing" замінюється на "zone_default". Інші рядки
// повертаються без змін (той самий об'єкт); вхідні об'єкти не мутуються.
export function applyDefaultZone(rows, zone) {
  return (rows || []).map((row) => {
    if (!row.issues?.some((i) => i.type === "zone_missing")) return row;
    return {
      ...row,
      values: { ...row.values, zone },
      issues: [
        ...row.issues.filter((i) => i.type !== "zone_missing"),
        { field: "zone", type: "zone_default" },
      ],
    };
  });
}

// Події обрізання для показу розрахунку: ті, що повернув DSS (clamped у
// відповіді), плюс обрізані ще під час розбору файлу (issues "clamped" --
// DSS таких значень уже не бачить). Без дублів за (studentId, criterionCode).
export function collectClampEvents(dssClamped, applicants) {
  const events = new Map();
  (dssClamped || []).forEach((e) => events.set(`${e.studentId}:${e.criterionCode}`, e));
  (applicants || []).forEach((a) => {
    (a.issues || [])
      .filter((i) => i.type === "clamped")
      .forEach((i) => {
        const key = `${a.id}:${i.field}`;
        if (!events.has(key)) {
          events.set(key, { studentId: a.id, criterionCode: i.field, original: i.original, value: a.values[i.field] });
        }
      });
  });
  return [...events.values()];
}

// Кількість рядків із даною issue (для блоків і звіту).
export function countRowsWithIssue(rows, type) {
  return (rows || []).filter((r) => r.issues?.some((i) => i.type === type)).length;
}

// Лічильники для звіту: обрізані значення (клітинки), рядки з віддаленістю за
// замовчуванням, рядки з пільгою за замовчуванням.
export function countImportIssues(rows) {
  return {
    noSession: countRowsWithIssue(rows, "no_session"),
    clamped: (rows || []).reduce((acc, r) => acc + (r.issues || []).filter((i) => i.type === "clamped").length, 0),
    zoneDefault: countRowsWithIssue(rows, "zone_default"),
    privilegeDefault: countRowsWithIssue(rows, "privilege_default"),
  };
}

// Формує payload для POST /api/dss/analyze -- лише числові дані per
// заявник (id, privilege, values.{ses,dorm,act,zone}). ПІБ і кімната
// НІКОЛИ не потрапляють у payload, навіть якщо є на вхідному об'єкті.
export function buildAnalyzePayload({ places, runType, applicants, normalization, details }) {
  return {
    places,
    runType,
    ...(normalization ? { normalization } : {}),
    ...(typeof details === "boolean" ? { details } : {}),
    applicants: (applicants || []).map((a) => ({
      id: a.id,
      privilege: a.privilege ?? DEFAULT_PRIVILEGE,
      values: {
        ses: a.values.ses,
        dorm: a.values.dorm,
        act: a.values.act,
        zone: a.values.zone,
      },
    })),
  };
}

// Значення критерію "як у файлі": для обрізаного понад шкалу -- початкове
// (issue clamped.original). Базове правило R = s + g + f (формула 1) -- це
// звичайна сума балів із файлу, тож 27 за діяльність лишається 27 (табл. 4
// статті), а модель (DSS) бачить уже обрізані 25.
function fileValue(applicant, field) {
  const clamped = (applicant.issues || []).find((i) => i.type === "clamped" && i.field === field);
  return clamped ? clamped.original : applicant.values[field];
}

// Ранжує заявників за сумою трьох показників (спадання), рівні суми --
// за id (детермінований порядок).
export function computeSumRanks(applicants) {
  const withSum = (applicants || []).map((a) => ({
    ...a,
    sum: fileValue(a, "ses") + fileValue(a, "dorm") + fileValue(a, "act"),
  }));
  const sorted = [...withSum].sort((a, b) => {
    if (b.sum !== a.sum) return b.sum - a.sum;
    return a.id - b.id;
  });
  const rankById = new Map();
  sorted.forEach((a, idx) => rankById.set(a.id, idx + 1));
  return withSum.map((a) => ({ ...a, sumRank: rankById.get(a.id) }));
}

export function statusFromRank(rank, places) {
  if (rank === null || rank === undefined) return null;
  return rank <= places ? "retains" : "loses";
}

// Зводить локальні дані (ПІБ, кімната, суми/ранг за сумою) з відповіддю
// бекенду (score/rank за моделлю) в один рядок звіту.
export function mergeReport({ applicants, analyzeResult, places }) {
  const withSum = computeSumRanks(applicants);
  // gurt-core (routes/dss.js::handleAnalyze) повертає чергу в полі "queue"
  // (і прохідний показник у "cutoffScore" нижче) -- не "results"/"cutoff".
  const results = analyzeResult?.queue || [];
  const resultById = new Map(results.map((r) => [r.id, r]));

  // Ранг за показником C без урахування пільги (порядок за спаданням score) --
  // лише для показу в табл. 6; місце в черзі (modelRank) визначає DSS.
  const scoreRankById = new Map(
    results
      .filter((r) => typeof r.score === "number")
      .sort((x, y) => y.score - x.score)
      .map((r, idx) => [r.id, idx + 1])
  );

  return withSum.map((a) => {
    const modelResult = resultById.get(a.id) || {};
    const modelRank = modelResult.rank ?? null;
    const score = modelResult.score ?? null;
    const sumStatus = statusFromRank(a.sumRank, places);
    const modelStatus = statusFromRank(modelRank, places);

    return {
      id: a.id,
      pib: a.pib,
      room: a.room,
      privilege: a.privilege ?? a.values?.privilege ?? DEFAULT_PRIVILEGE,
      issues: a.issues || [],
      values: a.values,
      // Група ранжування від DSS: "general" | "priority_<N>" | "no_session".
      groupKey: modelResult.groupKey ?? null,
      // Плоскі дублікати values.{ses,dorm,act} -- щоб узагальнене сортування
      // за ключем (sortReportRows: a[key]) працювало без спеціального
      // випадку для вкладених полів.
      ses: a.values.ses,
      dorm: a.values.dorm,
      act: a.values.act,
      zone: a.values.zone,
      sum: a.sum,
      sumRank: a.sumRank,
      sumStatus,
      score,
      dPlus: modelResult.dPlus ?? null,
      dMinus: modelResult.dMinus ?? null,
      scoreRank: scoreRankById.get(a.id) ?? null,
      // Проміжні величини TOPSIS від DSS ({raw, normalized, weighted, ideal,
      // antiIdeal}); null, якщо details не запитували або DSS недоступний.
      details: modelResult.details ?? null,
      modelRank,
      modelStatus,
      positionChange: modelRank !== null ? a.sumRank - modelRank : null,
      discrepancy: modelStatus !== null && modelStatus !== sumStatus,
    };
  });
}

export function findDiscrepancies(rows) {
  return (rows || []).filter((r) => r.discrepancy);
}

// Прохідний показник C* -- від бекенду, якщо є, інакше мінімальний
// показник серед тих, хто зберігає місце за моделлю.
export function computeCutoff(rows, analyzeResult) {
  if (analyzeResult && typeof analyzeResult.cutoffScore === "number") {
    return analyzeResult.cutoffScore;
  }
  const retained = (rows || []).filter(
    (r) => r.modelStatus === "retains" && typeof r.score === "number"
  );
  if (retained.length === 0) return null;
  return Math.min(...retained.map((r) => r.score));
}

export function buildSummary(rows, places) {
  const total = (rows || []).length;
  const retains = (rows || []).filter((r) => r.modelStatus === "retains").length;
  const loses = (rows || []).filter((r) => r.modelStatus === "loses").length;
  return { total, places, retains, loses };
}

export function sortReportRows(rows, key = "modelRank", direction = "asc") {
  const sign = direction === "desc" ? -1 : 1;
  return [...(rows || [])].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
    if (bv === null || bv === undefined) return -1;
    if (av < bv) return -1 * sign;
    if (av > bv) return 1 * sign;
    return 0;
  });
}

// Ключі, для яких застосовується числовий фільтр (діапазон від/до), і як
// дістати значення поля з рядка звіту. Винесено окремо від ImportReportTable
// (JSX), щоб фільтрацію можна було покрити тестами без збірки Babel.
export const NUMERIC_FILTER_FIELDS = {
  id: (r) => r.id,
  ses: (r) => r.values?.ses,
  dorm: (r) => r.values?.dorm,
  act: (r) => r.values?.act,
  zone: (r) => r.values?.zone,
  sum: (r) => r.sum,
  sumRank: (r) => r.sumRank,
  score: (r) => r.score,
  modelRank: (r) => r.modelRank,
  positionChange: (r) => r.positionChange,
};

// filters: { pib?: string, room?: string, modelStatus?: "all"|"retains"|"loses",
//            [numericKey]?: { min?: string|number, max?: string|number } }
// Порожній рядок/undefined/null у filters трактується як "фільтр не заданий".
export function applyReportFilters(rows, filters) {
  if (!filters) return rows || [];

  const textFilter = (value, needle) =>
    !needle || String(value ?? "").toLowerCase().includes(String(needle).toLowerCase());

  const numericFilter = (value, range) => {
    if (!range) return true;
    const { min, max } = range;
    if (min !== "" && min !== undefined && min !== null && !Number.isNaN(Number(min))) {
      if (value === null || value === undefined || value < Number(min)) return false;
    }
    if (max !== "" && max !== undefined && max !== null && !Number.isNaN(Number(max))) {
      if (value === null || value === undefined || value > Number(max)) return false;
    }
    return true;
  };

  return (rows || []).filter((r) => {
    if (!textFilter(r.pib, filters.pib)) return false;
    if (!textFilter(r.room, filters.room)) return false;
    if (filters.modelStatus && filters.modelStatus !== "all" && r.modelStatus !== filters.modelStatus) {
      return false;
    }
    for (const [key, getValue] of Object.entries(NUMERIC_FILTER_FIELDS)) {
      if (!numericFilter(getValue(r), filters[key])) return false;
    }
    return true;
  });
}

// Показник C і прохідний C* показуються з чотирма знаками скрізь: на
// реальних даних двоє студентів на межі мали 0,8193 і 0,8187, а з трьома
// знаками обидва виглядали як 0,819.
export const SCORE_DIGITS = 4;

// Число з десятковою комою (як у статті): formatComma(0.90421, 4) -> "0,9042".
export function formatComma(value, digits = 4) {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits).replace(".", ",") : "—";
}

export function formatScore(value) {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(SCORE_DIGITS) : "—";
}

// Претенденти без балів за сесію ранжуються окремою групою (groupKey від DSS).
export const NO_SESSION_KEY = "no_session";

export function splitNoSession(rows) {
  const list = rows || [];
  return {
    main: list.filter((r) => r.groupKey !== NO_SESSION_KEY),
    noSession: list.filter((r) => r.groupKey === NO_SESSION_KEY),
  };
}

// "Знеособити": ПІБ замінюється на "Студент <№>", кімната прибирається.
// Застосовується до рядків ДО сортування, фільтрації, таблиць і вивантаження,
// тож жоден з наступних кроків не бачить реального ПІБ.
export function anonymizeRow(row) {
  return { ...row, pib: `Здобувач ${row.id}`, room: "" };
}

export function anonymizeRows(rows, enabled) {
  return enabled ? (rows || []).map(anonymizeRow) : rows || [];
}

const statusText = (status) => (status === "loses" ? "втрачає місце" : "зберігає місце");

// Рядок аркуша "Звіт" у xlsx. При знеособленні колонки "Кімната" немає взагалі.
export function reportExportRow(r, { anonymize = false } = {}) {
  return {
    "№": r.id,
    ПІБ: r.pib || "",
    ...(anonymize ? {} : { Кімната: r.room || "" }),
    Сесія: r.values?.ses ?? "",
    Гуртожиток: r.values?.dorm ?? "",
    Діяльність: r.values?.act ?? "",
    Віддаленість: r.values?.zone ?? "",
    Пільга: r.privilege ?? "",
    Сума: r.sum ?? "",
    "Ранг за сумою": r.sumRank ?? "",
    "Показник TOPSIS": r.score ?? "",
    "Ранг за моделлю AHP+TOPSIS": r.modelRank ?? "",
    "Зміна позиції": r.positionChange ?? "",
    Група: r.groupKey ?? "",
    Статус: statusText(r.modelStatus),
  };
}

export function discrepancyExportRow(r, { anonymize = false } = {}) {
  return {
    ПІБ: r.pib || "",
    ...(anonymize ? {} : { Кімната: r.room || "" }),
    "Статус за сумою": statusText(r.sumStatus),
    "Статус за моделлю": statusText(r.modelStatus),
  };
}

// Рядки файлу, приведені до того, що реально зафіксовано в системі (пакет
// імпорту після «Зафіксувати»): лише рядки, які пакет не відхилив і не прибрав;
// віддаленість і пільга -- з пакета (їх могли виправити в попередньому перегляді
// або задати за замовчуванням на сервері). Так розрахунок після фіксації
// відповідає даним у БД, а не початковому файлу. Вхідні об'єкти не мутуються.
export function applyCommittedBatch(rows, batchRows) {
  const byNumber = new Map((batchRows || []).map((b) => [b.rowNumber, b]));
  return (rows || [])
    .filter((row) => !row.excluded)
    .flatMap((row) => {
      const b = byNumber.get(row.rowNumber);
      if (!b || b.matchStatus === "rejected") return [];
      const batchIssues = new Set((b.issues || []).map((i) => i.type));
      let issues = (row.issues || []).filter((i) => i.type !== "zone_missing" && i.type !== "zone_default");
      if (b.zoneIsDefault) issues = [...issues, { field: "zone", type: "zone_default" }];
      if (!batchIssues.has("privilege_default")) issues = issues.filter((i) => i.type !== "privilege_default");
      return [
        {
          ...row,
          values: {
            ...row.values,
            zone: b.zone ?? row.values.zone,
            privilege: b.privilege ?? row.values.privilege,
          },
          issues,
        },
      ];
    });
}
